import { fetchApi } from './api';
import type {
    ActualsQuery, ActualsResponse, AuditQuery, EvidenceQuery, EvidenceResponse, KpiDefinition, KpiPersonYear,
    KpiSelection, KpiTargetAuditRow, KpiTargetRow, PutTargetResult, RolloverPreview, RolloverResult, ScopeType,
} from '../types/strategicKpis';

const BASE = '/strategic-kpis';

/** K3: only non-empty lists go on the wire, as CSV. */
function setSelection(params: URLSearchParams, s: KpiSelection | null | undefined) {
    if (!s) return;
    if (s.companies.length) params.set('companies', s.companies.join(','));
    if (s.teams.length) params.set('teams', s.teams.join(','));
    if (s.persons.length) params.set('persons', s.persons.join(','));
}

/**
 * Client of /api/v1/strategic-kpis. Reads (P3) and the target write path of the Targets tab
 * (P4): persons of a year, stored targets, PUT one target, rollover, audit trail.
 */
export const strategicKpisApi = {
    actuals: (q: ActualsQuery, nocache = false) => {
        const params = new URLSearchParams({ bu: q.bus.join(','), year: String(q.year), asOf: q.asOf });
        setSelection(params, q.selection);
        if (nocache) params.set('nocache', '1');
        return fetchApi<ActualsResponse>(`${BASE}/actuals?${params.toString()}`);
    },

    evidence: (q: EvidenceQuery) => {
        const params = new URLSearchParams({ kpi: q.kpiKey, year: String(q.year), asOf: q.asOf });
        if (q.scopeType === 'PERSON') params.set('staffId', q.scopeKey);
        else if (q.scopeType === 'SELECTION') params.set('scopeType', 'SELECTION');
        else { params.set('scopeType', q.scopeType); params.set('scopeKey', q.scopeKey); }
        if (q.bus?.length) params.set('bu', q.bus.join(','));
        setSelection(params, q.selection);
        return fetchApi<EvidenceResponse>(`${BASE}/evidence?${params.toString()}`);
    },

    definitions: (includeInactive = false) =>
        fetchApi<KpiDefinition[]>(`${BASE}/definitions${includeInactive ? '?includeInactive=true' : ''}`),

    /**
     * K3b (E9): the persons who count in the year. A person flagged IsExcluded (Kadir since
     * migration 220) counts nowhere — the engine drops him before every aggregation, so he is left
     * out of the filter lists and the Targets tab here as well. His stored targets stay (Power BI).
     */
    persons: (year: number) => fetchApi<KpiPersonYear[]>(`${BASE}/persons?year=${year}`).then(rows => rows.filter(p => !p.IsExcluded)),

    targets: (year: number) => fetchApi<KpiTargetRow[]>(`${BASE}/targets?year=${year}`),

    /** value = null removes the target. Numbers only — the API refuses strings (P1 rule). */
    putTarget: (kpiKey: string, year: number, scopeType: ScopeType, scopeKey: string, value: number | null, note?: string | null) =>
        fetchApi<PutTargetResult>(`${BASE}/targets/${encodeURIComponent(kpiKey)}/${year}/${scopeType}/${encodeURIComponent(scopeKey)}`, {
            method: 'PUT',
            body: JSON.stringify({ value, note: note ?? null }),
        }),

    rollover: (sourceYear: number, targetYear: number, scopeTypes?: ScopeType[]) =>
        fetchApi<RolloverResult>(`${BASE}/targets/rollover`, {
            method: 'POST',
            body: JSON.stringify(scopeTypes ? { sourceYear, targetYear, scopeTypes } : { sourceYear, targetYear }),
        }),

    /** C1 (E10): the same rollover as a SELECT-only simulation — writes nothing. */
    previewRollover: (sourceYear: number, targetYear: number, scopeTypes?: ScopeType[]) =>
        fetchApi<RolloverPreview>(`${BASE}/targets/rollover`, {
            method: 'POST',
            body: JSON.stringify(scopeTypes
                ? { sourceYear, targetYear, scopeTypes, dryRun: true }
                : { sourceYear, targetYear, dryRun: true }),
        }),

    audit: (q: AuditQuery) => {
        const params = new URLSearchParams();
        if (q.year !== undefined) params.set('year', String(q.year));
        if (q.kpiKey) params.set('kpiKey', q.kpiKey);
        if (q.scopeType) params.set('scopeType', q.scopeType);
        if (q.scopeKey) params.set('scopeKey', q.scopeKey);
        if (q.limit !== undefined) params.set('limit', String(q.limit));
        const qs = params.toString();
        return fetchApi<KpiTargetAuditRow[]>(`${BASE}/targets/audit${qs ? '?' + qs : ''}`);
    },
};
